import { useCallback, useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";
import Svg, { Circle, Defs, Line, Mask, Path, Rect } from "react-native-svg";
import Animated, {
  Extrapolation,
  interpolate,
  runOnJS,
  type SharedValue,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { theme } from "@theme";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const AnimatedLine = Animated.createAnimatedComponent(Line);
const AnimatedPath = Animated.createAnimatedComponent(Path);

const CENTER = 21;
const ICON_RADIUS = 11.5;
const CHECK_PATH_LENGTH = 13;
const PARTICLE_DISTANCE = 19;
const SAVE_ANIMATION_DURATION_MS = 600;
const PARTICLE_ANGLES = [-90, -45, 0, 45, 90, 135, 180, 225];
const OUTLINE_DASH_PATTERN = [53, 4, 4.5, 4, 4.5, 2.3];
const SAVED_COLOR = theme.colors.saveBlue;

type SaveParticleProps = {
  angle: number;
  progress: SharedValue<number>;
};

const SaveParticle = ({ angle, progress }: SaveParticleProps) => {
  const animatedProps = useAnimatedProps(() => {
    const spiralAngle =
      angle +
      interpolate(progress.value, [0, 0.76], [-70, 0], Extrapolation.CLAMP);
    const radians = (spiralAngle * Math.PI) / 180;
    const distance = interpolate(
      progress.value,
      [0, 0.94],
      [ICON_RADIUS - 1, PARTICLE_DISTANCE],
      Extrapolation.CLAMP,
    );

    return {
      cx: CENTER + Math.cos(radians) * distance,
      cy: CENTER + Math.sin(radians) * distance,
      opacity: interpolate(
        progress.value,
        [0, 0.44, 0.96],
        [0, 1, 0],
        Extrapolation.CLAMP,
      ),
      r: interpolate(
        progress.value,
        [0, 0.48, 0.96],
        [0, 1.65, 0.45],
        Extrapolation.CLAMP,
      ),
    };
  });

  return <AnimatedCircle animatedProps={animatedProps} fill={SAVED_COLOR} />;
};

type SaveIconProps = {
  animationTrigger: number;
  saved: boolean;
};

const SaveIcon = ({ animationTrigger, saved }: SaveIconProps) => {
  const progress = useSharedValue(saved ? 1 : 0);
  const latestSaved = useRef(saved);
  const isCelebrating = useRef(false);
  const previousTrigger = useRef(animationTrigger);

  const finishCelebration = useCallback(() => {
    isCelebrating.current = false;
    progress.set(
      withTiming(latestSaved.current ? 1 : 0, {
        duration: 120,
      }),
    );
  }, [progress]);

  useEffect(() => {
    if (previousTrigger.current === animationTrigger) {
      return;
    }

    previousTrigger.current = animationTrigger;
    isCelebrating.current = true;
    progress.set(0);
    progress.set(
      withTiming(1, { duration: SAVE_ANIMATION_DURATION_MS }, (finished) => {
        if (finished) {
          runOnJS(finishCelebration)();
        }
      }),
    );
  }, [animationTrigger, finishCelebration, progress]);

  useEffect(() => {
    latestSaved.current = saved;
    if (!isCelebrating.current) {
      progress.set(saved ? 1 : 0);
    }
  }, [progress, saved]);

  const outlineProps = useAnimatedProps(() => ({
    opacity: interpolate(
      progress.value,
      [0, 0.3, 0.5],
      [1, 1, 0],
      Extrapolation.CLAMP,
    ),
    r: interpolate(
      progress.value,
      [0, 0.28, 0.5],
      [ICON_RADIUS, ICON_RADIUS, ICON_RADIUS * 0.66],
      Extrapolation.CLAMP,
    ),
    strokeDashoffset: interpolate(
      progress.value,
      [0, 0.32, 0.5],
      [0, -15, -22],
      Extrapolation.CLAMP,
    ),
  }));

  const verticalPlusProps = useAnimatedProps(() => {
    const halfLength = interpolate(
      progress.value,
      [0, 0.25],
      [4.5, 0],
      Extrapolation.CLAMP,
    );

    return {
      y1: CENTER - halfLength,
      y2: CENTER + halfLength,
      opacity: interpolate(
        progress.value,
        [0.12, 0.28],
        [1, 0],
        Extrapolation.CLAMP,
      ),
    };
  });

  const horizontalPlusProps = useAnimatedProps(() => {
    const halfLength = interpolate(
      progress.value,
      [0.08, 0.3],
      [4.5, 1.2],
      Extrapolation.CLAMP,
    );

    return {
      x1: CENTER - halfLength,
      x2: CENTER + halfLength,
      opacity: interpolate(
        progress.value,
        [0.18, 0.34],
        [1, 0],
        Extrapolation.CLAMP,
      ),
    };
  });

  const fillProps = useAnimatedProps(() => ({
    opacity: interpolate(
      progress.value,
      [0.26, 0.48],
      [0, 1],
      Extrapolation.CLAMP,
    ),
    r: interpolate(
      progress.value,
      [0.26, 0.46, 0.58],
      [0, ICON_RADIUS * 1.08, ICON_RADIUS],
      Extrapolation.CLAMP,
    ),
  }));

  const checkCutoutProps = useAnimatedProps(() => ({
    opacity: interpolate(
      progress.value,
      [0.4, 0.48],
      [0, 1],
      Extrapolation.CLAMP,
    ),
    strokeDashoffset: interpolate(
      progress.value,
      [0.44, 0.76],
      [CHECK_PATH_LENGTH, 0],
      Extrapolation.CLAMP,
    ),
  }));
  return (
    <View style={styles.container}>
      <Svg width={42} height={42} viewBox="0 0 42 42" style={styles.canvas}>
        <Defs>
          <Mask id="savedCircleMask">
            <Rect x="0" y="0" width="42" height="42" fill="white" />
            <AnimatedPath
              d="M16.8 21.2 L19.8 24.2 L25.7 17.8"
              fill="none"
              stroke="black"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={CHECK_PATH_LENGTH}
              animatedProps={checkCutoutProps}
            />
          </Mask>
        </Defs>

        {PARTICLE_ANGLES.map((angle) => (
          <SaveParticle key={angle} angle={angle} progress={progress} />
        ))}

        <AnimatedCircle
          cx={CENTER}
          cy={CENTER}
          fill={SAVED_COLOR}
          mask="url(#savedCircleMask)"
          animatedProps={fillProps}
        />

        <AnimatedCircle
          cx={CENTER}
          cy={CENTER}
          fill="none"
          stroke="white"
          strokeWidth={1.25}
          strokeDasharray={OUTLINE_DASH_PATTERN}
          strokeLinecap="round"
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
          animatedProps={outlineProps}
        />
        <AnimatedLine
          x1={CENTER}
          x2={CENTER}
          stroke="white"
          strokeWidth={1.7}
          strokeLinecap="round"
          animatedProps={verticalPlusProps}
        />
        <AnimatedLine
          y1={CENTER}
          y2={CENTER}
          stroke="white"
          strokeWidth={1.7}
          strokeLinecap="round"
          animatedProps={horizontalPlusProps}
        />
      </Svg>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 26,
    height: 26,
    overflow: "visible",
  },
  canvas: {
    position: "absolute",
    left: -8,
    top: -8,
  },
});

export default SaveIcon;
